import { useEffect, useRef } from 'react'
import { useBibleStore } from '../stores/bibleStore'
import { getBookById, getNextChapter, getPrevChapter } from '../lib/bibleBooks'
import { TRANSLATIONS } from '../lib/bibleConstants'

export default function BibleReaderPanel() {
  const readerPanel = useBibleStore((s) => s.readerPanel)
  const translation = useBibleStore((s) => s.translation)
  const closeReaderPanel = useBibleStore((s) => s.closeReaderPanel)
  const readerGoNext = useBibleStore((s) => s.readerGoNext)
  const readerGoPrev = useBibleStore((s) => s.readerGoPrev)
  const readerSetTranslation = useBibleStore((s) => s.readerSetTranslation)
  const readerGoToChapter = useBibleStore((s) => s.readerGoToChapter)

  const contentRef = useRef<HTMLDivElement>(null)
  const book = readerPanel.bookId ? getBookById(readerPanel.bookId) : null
  const hasNext = readerPanel.bookId && readerPanel.chapter ? !!getNextChapter(readerPanel.bookId, readerPanel.chapter) : false
  const hasPrev = readerPanel.bookId && readerPanel.chapter ? !!getPrevChapter(readerPanel.bookId, readerPanel.chapter) : false

  useEffect(() => {
    if (!readerPanel.content || !readerPanel.verse || !contentRef.current) return
    const el = contentRef.current.querySelector(
      `[data-verse-number="${readerPanel.verse}"], .v[data-number="${readerPanel.verse}"]`
    )
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      // Use inline style instead of classList to avoid Tailwind JIT purge issues
      ;(el as HTMLElement).style.backgroundColor = 'rgba(212, 168, 67, 0.2)'
      ;(el as HTMLElement).style.borderRadius = '4px'
      ;(el as HTMLElement).style.padding = '0 4px'
    }
  }, [readerPanel.content, readerPanel.verse])

  useEffect(() => {
    if (contentRef.current && readerPanel.content && !readerPanel.verse) {
      contentRef.current.scrollTop = 0
    }
  }, [readerPanel.content, readerPanel.verse])

  if (!readerPanel.open) return null

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={closeReaderPanel}
      />

      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-bg border-l border-elevated z-50 flex flex-col animate-slide-in-right">
        <div className="flex items-center justify-between px-4 py-3 border-b border-elevated shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={closeReaderPanel}
              className="text-text-secondary hover:text-text-primary transition-colors shrink-0"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="font-semibold truncate">
              {book?.name} {readerPanel.chapter}
            </h2>
          </div>
          <select
            value={translation}
            onChange={(e) => readerSetTranslation(e.target.value)}
            className="bg-elevated text-text-primary text-sm rounded-lg px-2 py-1.5 border border-surface focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {TRANSLATIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
          {readerPanel.loading && (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {readerPanel.error && (
            <div className="text-center py-16">
              <p className="text-error mb-2">{readerPanel.error}</p>
              <button
                onClick={() => {
                  if (readerPanel.bookId && readerPanel.chapter)
                    readerGoToChapter(readerPanel.bookId, readerPanel.chapter)
                }}
                className="text-accent text-sm hover:text-accent-hover transition-colors"
              >
                Try again
              </button>
            </div>
          )}

          {!readerPanel.loading && !readerPanel.error && readerPanel.content && (
            <>
              <div
                className="bible-content text-text-primary leading-relaxed text-base"
                dangerouslySetInnerHTML={{ __html: readerPanel.content.content }}
              />

              <div className="flex items-center justify-between py-6 mt-4 border-t border-surface">
                {hasPrev ? (
                  <button
                    onClick={readerGoPrev}
                    className="flex items-center gap-1 text-accent text-sm hover:text-accent-hover transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                    Previous
                  </button>
                ) : <div />}
                {hasNext ? (
                  <button
                    onClick={readerGoNext}
                    className="flex items-center gap-1 text-accent text-sm hover:text-accent-hover transition-colors"
                  >
                    Next
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                ) : <div />}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
